import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Loading } from "../../components/shared/loading";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertPhotoOutlinedIcon from "@material-ui/icons/InsertPhotoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SaveIcon from "@material-ui/icons/Save";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";
const PRIORITY_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);

export const EditFeedback = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [projname, setProjName] = useState("");
  const [name, setName] = useState("");
  const [priority, setPriority] = useState("15");
  const [oldImage, setOldImage] = useState("");
  const [subj, setSubj] = useState("");
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState(true);
  const [testPic, setTestPic] = useState({ raw: "", preview: "" });

  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const getData = () => {
    const formData = new FormData();
    formData.append("id", id);
    Axios.post("/api/getsingletestms", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        const d = res.data.data;
        setProjName(d.test_name || "");
        setName(d.test_prof || "");
        setSubj(d.test_sub || "");
        setMsg(d.test_msg || "");
        setPriority(String(d.test_priority || 15));
        setOldImage(d.test_image || "");
        setStatus(d.test_status === 1);
        setIsLoading(false);
      } else {
        toast.error("Could not load testimonial");
        navigate("/dash/alltestimonials");
      }
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please select only JPEG, JPG, PNG or WebP images.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB.");
      return;
    }
    setTestPic({
      preview: URL.createObjectURL(file),
      raw: file,
    });
  };

  const onSubmit = () => {
    setErr(0);
    if (!projname.trim()) {
      setErr(1);
      toast.error("Please enter the project / institute name.");
      return;
    }
    if (!name.trim()) {
      setErr(2);
      toast.error("Please enter your name with designation.");
      return;
    }
    if (!subj.trim()) {
      setErr(4);
      toast.error("Please enter the subject.");
      return;
    }
    if (!msg.trim()) {
      setErr(5);
      toast.error("Please enter the message.");
      return;
    }
    if (!oldImage && !testPic.raw) {
      setErr(3);
      toast.error("Please select an image.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    if (testPic.raw) formData.append("testPic", testPic.raw);
    formData.append("id", id);
    formData.append("oldimage", oldImage);
    formData.append("name", projname.trim());
    formData.append("profname", name.trim());
    formData.append("priority", priority);
    formData.append("sub", subj.trim());
    formData.append("msg", msg.trim());
    formData.append("status", status ? "1" : "0");

    Axios.post("/api/updatetest", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Updated");
          setTestPic({ raw: "", preview: "" });
          setIsLoading(true);
          getData();
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>Edit Feedback - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Testimonials"
        title="Edit / approve feedback"
        subtitle="Update the testimonial details and approval status."
        backTo="/dash/alltestimonials"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Project / Institute name"
            variant="outlined"
            error={err === 1}
            value={projname}
            size="small"
            onChange={(e) => setProjName(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name with designation"
            variant="outlined"
            error={err === 2}
            value={name}
            size="small"
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Subject"
            variant="outlined"
            error={err === 4}
            value={subj}
            size="small"
            onChange={(e) => setSubj(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Priority"
            variant="outlined"
            value={priority}
            size="small"
            onChange={(e) => setPriority(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          >
            {PRIORITY_OPTIONS.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.statusRow}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <span>
              Status:{" "}
              <span className={status ? "on" : "off"}>
                {status ? "Visible" : "Hidden"}
              </span>
            </span>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Message"
            variant="outlined"
            error={err === 5}
            value={msg}
            size="small"
            multiline
            rows={4}
            onChange={(e) => setMsg(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Avatar image</Typography>
          <Box className={classes.uploadWrap}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
              id="testimonial-image-upload"
              type="file"
            />
            <label
              htmlFor="testimonial-image-upload"
              style={{ display: "block" }}
            >
              <Box
                className={`${classes.uploadBox} ${
                  testPic.raw ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {testPic.raw ? (
                    <InsertPhotoOutlinedIcon />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )}
                </Box>
                <Box className={classes.uploadInfo}>
                  {testPic.raw ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon
                          style={{ fontSize: 16, color: "#10b981" }}
                        />
                        <strong>{testPic.raw.name}</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span>
                        <strong>
                          {oldImage ? "Replace avatar" : "Upload avatar"}
                        </strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        JPEG, PNG or WebP, up to 5 MB
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>
            <Box style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {oldImage && !testPic.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    Current image
                  </Typography>
                  <img
                    src={"/static/testms/" + oldImage}
                    alt="current"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 160, maxHeight: 160 }}
                  />
                </Box>
              )}
              {testPic.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    New image preview
                  </Typography>
                  <img
                    src={testPic.preview}
                    alt="preview"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 160, maxHeight: 160 }}
                  />
                </Box>
              )}
            </Box>
            {err === 3 && (
              <Typography style={{ color: "#dc2626", fontSize: "0.78rem" }}>
                Please select an image.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/alltestimonials")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          startIcon={!submitting && <SaveIcon />}
        >
          {submitting ? "Saving..." : "Save changes"}
        </Button>
      </Box>
    </div>
  );
};
